/**
 * @file     homeValueFlow.ts
 * @readme   ../../CLAUDE.md
 * @purpose  English strings for the Home Value / Value Pulse seller lead flow: SEO, step labels, and confirmation copy.
 */
export const homeValueFlow = {
  seo: {
    title: 'BC Home Valuation | Value Pulse',
    description:
      "Get a rough starting range for your home based on its location, property type, bedrooms, bathrooms, and similar active listings.",
    ogTitle: 'See What Your Home Could Sell For',
    ogDescription:
      'Share a few details about your home to see a price range based on similar homes for sale.'
  },
  page: {
    title: 'See what your home could sell for',
    description:
      'Share a few details about your home to get a rough range based on similar homes for sale nearby.'
  },
  actions: {
    continue: 'Continue'
  },
  entry: {
    eyebrow: 'For homeowners',
    title: 'See a starting range for your home',
    benefitRange: 'See asking prices for similar homes for sale nearby.',
    cta: 'Start your valuation'
  },
  address: {
    title: 'Where is the home?',
    label: 'Property address',
    placeholder: 'Start typing the property address',
    unitLabel: 'Unit or suite',
    unitPlaceholder: 'Unit 402',
    cityLabel: 'City',
    cityPlaceholder: 'Vancouver',
    provinceLabel: 'Province',
    postalLabel: 'Postal code',
    postalPlaceholder: 'V6B 1A1',
    findLocation: 'Use current location',
    findingLocation: 'Finding...',
    reviewLocation: 'Review detected address',
    locationFound: 'Is this the right address?',
    acceptLocation: 'Use this address',
    rejectLocation: 'Not this address',
    locationDenied:
      'Location access was not allowed. Enter the address manually or enable location access in your browser.',
    locationTimeout:
      'Your location took too long to respond. Try again or enter the address manually.',
    locationUnavailable: 'Your location is unavailable. Enter the address manually.',
    locationNotFound: 'A precise address could not be found. Enter it manually instead.',
    locationLookupFailed:
      'The address lookup could not be completed. Try again or enter it manually.'
  },
  ownsProperty: {
    label: 'Do you own this property?',
    yes: 'Yes',
    no: 'No'
  },
  timeline: {
    label: 'When are you hoping to sell?',
    asap: 'As soon as possible',
    oneToThree: '1 to 3 months',
    threeToSix: '3 to 6 months',
    later: 'Further out'
  },
  photos: {
    gateTitle: 'Want to add photos or notes?',
    gateDescription: 'Both are optional and help the estimate.',
    gateAccept: 'Yes, add them',
    gateDecline: 'Skip this',
    title: 'Add what you have',
    description: 'Recent photos and anything unusual about the home.',
    notesLabel: 'Notes (optional)',
    notesPlaceholder: 'Renovations, a tenanted suite, anything a photo would not show.'
  },
  capture: {
    unifiedTitle: 'Where should your valuation go?',
    unifiedDescription:
      'Your range is linked to the email you give.',
    nameLabel: 'Name',
    namePlaceholder: 'Your full name',
    firstNameLabel: 'First name',
    firstNamePlaceholder: 'Your first name',
    firstNameRequired: 'First name is required',
    lastNameLabel: 'Last name',
    lastNamePlaceholder: 'Your last name',
    lastNameRequired: 'Last name is required',
    emailLabel: 'Email',
    emailPlaceholder: 'name@email.com',
    phoneLabel: 'Phone',
    phoneOptionalLabel: 'Phone (optional)',
    phoneOptionalHelper: 'For a faster reply by text.',
    phonePlaceholder: '(604) 555-0123',
    unifiedSubmit: 'Get your estimated range',
    rangeTitle: 'Get your starting range',
    rangeSubmit: 'See my range',
    rangeVerifyHeadline: 'Confirm your email to see your range',
    rangeVerifyBody: 'Open the link sent to {{email}}. Your range opens here as soon as you do.',
    optInTitle: 'Open your comparative market analysis',
    optInDescription:
      'The analysis opens on your dashboard after you confirm your email.',
    optInSubmit: 'Email me the link',
    verifyTitle: 'Confirm your email to open the analysis',
    verifyBody:
      'Open the link sent to {{email}} to see your analysis.',
    success:
      'Thanks, {{name}}. You will get your valuation after your details are reviewed.'
  },
  propertyType: {
    title: 'Property type',
    condo: 'Condo',
    townhouse: 'Townhouse',
    detached: 'Detached',
    other: 'Other'
  },
  estimate: {
    title: 'Your home’s starting range',
    previewTitle: 'Your starting range',
    previewCta: 'Get the full analysis',
    explainer:
      'For Greater Vancouver homes, the full analysis adds recent nearby sales.',
    noBand:
      'Not enough similar homes for sale nearby for a quick range.',
    noRange: 'There is no quick range for this address.',
    soldsCta: 'Get comparable solds',
    soldsCheckEmail: 'Check your email to open your comparable solds.',
    soldsOpen: 'Open comparable solds'
  },
  valueRange: {
    basisBuilding: 'Based on {{count}} similar {{homes}} for sale in your building.',
    basisNearby: 'Based on {{count}} similar {{homes}} for sale within {{km}} km.',
    basisAreaSize: 'Based on the price per square foot of {{homes}} for sale in {{area}}.',
    basisAreaBeds: 'Based on {{count}} similar {{homes}} for sale in {{area}}.',
    basisArea: 'Based on {{homes}} for sale in {{area}}.',
    basisRegion: 'Based on {{homes}} for sale within {{km}} km.',
    basisRegionArea: 'Based on {{homes}} for sale near {{area}}.',
    basisProvince: 'Based on {{homes}} for sale across BC.',
    homes: {
      condo: 'condos',
      townhouse: 'townhouses',
      detached: 'detached homes',
      other: 'homes'
    },
    eyebrow: 'Starting range from active listings in {{city}}',
    eyebrowArea: 'Starting range from all active listings in {{city}}',
    range: '{{low}} to {{high}}',
    body: 'The asking range across {{count}} similar listings, with a median around {{median}}.',
    disclaimer:
      'From asking prices of current MLS® listings. Not an appraisal or an opinion of your home’s value.'
  },
  ready: {
    title: 'Check your email',
    body: 'Open the link in your email to see your analysis.'
  },
  progress: {
    label: 'Step {{current}} of {{total}}'
  }
} as const;
